<template>
    <page-template page-id="ConfirmRegistration" ref="super">

        <document-title :title="$tb('pages.confirmRegistration.documentTitle')"></document-title>

        <template v-slot:before v-if="isTitlebarActive">
            <titlebar :text="$tb('pages.confirmRegistration.header', {0: $store.state.appName})"></titlebar>
        </template>

        <div class="ui column">

            <brand-header :text="$t('pages.confirmRegistration.title')"
                          :has-extension="hasHeaderExtension"
                          :has-logo="hasBrandLogo">
            </brand-header>

            <p v-html=" $t('pages.confirmRegistration.info', {0: unconfirmedUsername || username })"></p>

            <messages id="Notifications" ref="postman"></messages>

            <validated-field :show-errors-on="showErrors" :has-any-errors="$v.code.$invalid"
                             :validation-rules="[{value: $v.code.notEmpty, msg: $t('common.validation.otp.empty')},
                             {value: $v.code.hasFormat, showOn: $v.code.notEmpty, msg: $t('common.validation.otp.format')}]">
                <tan-input-field id="tan" v-model="$v.code.$model" ref="tanInput"
                                 :aria-label-tan-type="$t('aria.common.confirmationCode')"
                                 :invalid="$v.code.$invalid && showErrors" @focusout="otpFocusOut"></tan-input-field>
            </validated-field>

            <div class="field">
                <button id="confirm" type="submit" class="ui fluid large primary submit button"
                        @click.prevent="onConfirm">
                    {{$t('common.button.confirm')}}
                </button>
            </div>

            <h4 class="custom-ui header" v-html="$t('pages.confirmRegistration.resendCode.header')"></h4>

            <p v-if="isMobileLoginDisabled" v-html="$t('pages.confirmRegistration.resendCode.textEmail')"></p>

            <p v-else v-html="$t('pages.confirmRegistration.resendCode.text')"></p>
            <p>
              {{$t(this.isEmailAddress ? 'pages.confirmRegistration.editEmail' : 'pages.confirmRegistration.editMobile')}}"{{$t( this.isEmailAddress ? 'common.button.editEmail' : 'common.button.editMobile')}}"
            </p>
            <div class="field">
                <resend-button id="resend" ref="resendBtn"
                               :value="$t('common.button.resend')"
                               :action="resendConfirmationCode"></resend-button>
            </div>
            <div class="field">
                <button id="editUsername" type="submit" class="ui fluid basic button"
                        @click.prevent="onEditUsername">
                    {{$t( this.isEmailAddress ? 'common.button.editEmail' : 'common.button.editMobile')}}
                </button>
            </div>
        </div>
    </page-template>
</template>

<script>
import PageTemplate from './template/PageTemplate.vue'
import Titlebar from '../components/Titlebar.vue'
import BrandHeader from '../components/BrandHeader.vue'
import ResendButton from '../components/ResendButton.vue'
import TanInputField from '../components/TanInputField.vue'
import Messages from '../components/Messages.vue'
import ValidatedField from '../components/ValidatedField.vue'
import { mixin } from '../js/ciam-mixin.js'
import { brand } from '../js/brand.js'
import { getRecaptchaToken } from '../js/helpers.js'

export default {
  components: {
    PageTemplate,
    Titlebar,
    BrandHeader,
    ResendButton,
    TanInputField,
    Messages,
    ValidatedField
  },
  mixins: [mixin, brand],
  props: {
    codeSent: {
      type: Boolean,
      default: true
    },
    unconfirmedUsername: {
      type: String,
      default: ''
    }
  },
  validations () {
    return {
      code: {
        notEmpty (value) {
          return !!value && value !== '------'
        },
        hasFormat (value) {
          return /[\d]{6}/.test(value) // otp needs to be 6 digits
        }
      }
    }
  },
  data: function () {
    return {
      username: this.unconfirmedUsername || this.$store.state.unconfirmedUsername || this.$store.state.username,
      uid: this.$store.state.uid,
      code: null,
      showErrors: false,
      isRecaptchaActive: this.$store.state.isRecaptchaActive,
      recaptchaSiteKey: this.$store.state.recaptchaSiteKey,
      isMobileLoginDisabled: this.$store.state.isMobileLoginDisabled,
      isEmailAddress: false
    }
  },
  methods: {
    onConfirm: function (e) {
      // remove previous errors
      this.$refs.postman.clear()
      this.showErrors = false
      this.username = this.username?.replace(/＠/g, '@')

      if (!this.isFormValid()) return

      this.$refs.super.startLoading()

      const payload = this.$api.payloads.buildConfirmUsernamePayload(this.username, this.code)

      this.$api.register.confirmUsername(this.uid, payload, this.handleRoutingResponse, this.onFail)
    },
    onEditUsername: function (e) {
      if (this.isEmailAddress) {
        this.$router.push({
          name: 'GOTO_SET_EMAIL'
        })
      } else {
        this.$router.push({
          name: 'GOTO_SET_MOBILE'
        })
      }
    },
    isFormValid: function () {
      if (!this.$v.$anyDirty || this.$v.$anyError || this.$v.$invalid) {
        const _this = this

        this.focusFirstErrorField(function () {
          _this.showErrors = true
        })

        return false
      }

      return true
    },
    onFail: function (response) {
      this.onAjaxError(response)
      this.$refs.super.stopLoading()
    },
    sendConfirmationCode: async function () {
      const recaptchaToken = await getRecaptchaToken(this.isRecaptchaActive, this.recaptchaSiteKey, this.$recaptcha.actions.CONFIRM_REGISTRATION_OTP)
      this.$api.codes.sendUsernameConfirmation(this.username, recaptchaToken, null, this.showCodeNotification)
    },
    resendConfirmationCode: async function () {
      this.$refs.super.startLoading()
      this.$refs.postman.clear()

      const recaptchaToken = await getRecaptchaToken(this.isRecaptchaActive, this.recaptchaSiteKey, this.$recaptcha.actions.CONFIRM_REGISTRATION_OTP)

      this.$api.codes.sendUsernameConfirmation(this.username, recaptchaToken, this.handleResendResponse, this.onFail)
    },
    handleResendResponse: function () {
      this.$refs.tanInput.reset()// reset the TAN input
      this.$refs.resendBtn.disableActionForTime(30)
      this.$refs.postman.showMessage('info', this.$t('common.msg.resendOk', {
        0: this.$tb('common.label.confirmationCode'),
        1: this.username
      }))
      this.$refs.super.stopLoading()
      this.$refs.tanInput.focus()
    },
    showCodeNotification: function () {
      this.$refs.postman.showMessage('info', this.$t('pages.confirmRegistration.codeSendingFailed'))
    },
    checkHideErrors: function () {
      if (this.$v.$invalid) return
      $('form .ui.error.message').empty()
      this.showErrors = false
    },
    otpFocusOut: function () {
      $('#confirm').focus()
    },
    init: function () {
      const untrustedUsername = this.unconfirmedUsername || this.$store.state.unconfirmedUsername
      this.isEmailAddress = this.unconfirmedUsername ? this.isEmail(untrustedUsername) : this.isEmail(this.username)
      if (this.codeSent === false) {
        console.info("Sending confirmation code, didn't work before")
        this.sendConfirmationCode()
      }

      this.$refs.tanInput.focus()
    }
  },
  mounted: function () {
    console.info(`Confirm username [uid=${this.uid}, username=${this.username}, codeSent=${this.codeSent}, hasPass=${this.hasPass}]`)
    this.init()
  },
  watch: {
    $v: {
      handler () {
        this.checkHideErrors()
      },
      deep: true
    }
  }
}
</script>

<style scoped>
</style>
